// SEQ-180 phase 2 widget config, used from phase 1 (plan decision D7: "Phase
// 1 widgets are hardcoded instances of the same config object, so phase 2
// saves what phase 1 already renders"). Every chart on the analytics page is
// built from one of these, whether or not it is user-editable yet.
//
// Field list is deliberately exactly what plan section 6.7 and the phase 2
// research specify: schemaVersion, name, chartType, metric, secondaryMetric,
// groupBy, aggregation, filters, dateRange, comparison, sort, limit,
// displayAs. `dateRange` reuses DatePresetName / DateRange from
// fiscalYear.ts, so a widget's date range and a filter bar preset are the
// same value with no translation layer between them.
//
// `groupBy` reuses `PnlRollupGrain` from pnlAnalytics.ts, NOT `PnlGroupBy`
// from analyticsClient.ts (Round 3, code review, parity harness divergence
// 4). A widget describes how ITS chart displays data once fetched (one KPI
// card's "company" total, a table's "project" rows), which is a client-side
// rollup concern; it is not the same thing as the grain a page fetches from
// the RPC (analyticsClient.ts's `PnlGroupBy`, the RPC's exact CHECK
// constraint vocabulary, which has no 'company' value at all). A page loads
// data at an RPC-supported grain, then a widget's `groupBy` tells
// `computePnlBuckets` how to roll that data up for its own display, which is
// exactly why PHASE1_WIDGETS' four KPI cards below use `groupBy: "company"`.

// SEQ-180 PHASE 2 (plan D15, D16): `schemaVersion` stays 1 and only OPTIONAL
// fields are added (`dataGrain`, `layout`, `dateRangeMode`,
// `dateRange.customPresetId`). Every phase 1 config stays valid with no
// upcast. The rule this locks: the first REQUIRED field, or the first
// changed meaning of an existing field, bumps to `schemaVersion: 2` with an
// `upgradeWidgetConfig` function and a decode test.

import type { DatePresetName, DateRange } from "./fiscalYear";
import { validateGridConfig, type PnlGridConfig } from "./pnlGridConfig";
import { PNL_GROUP_BY_VALUES, type PnlGroupBy } from "./analyticsClient";
import {
  resolveAggregationMethod,
  resolveMetricUnit,
  type PnlAggregationMethod,
  type PnlMetricDefinition,
  type PnlRollupGrain,
  type PnlUnit,
} from "./pnlAnalytics";

// SEQ-193: "month_on_month" joins the vocabulary now that the dashboard
// builder can lay one or more metrics (company-wide) or one metric's
// projects out as rows, with a calendar month per column. It is its own
// chart type rather than a `table` variant because its data shape is
// fundamentally different (many values per row, one per month, not one
// value plus an optional secondary), so it needs its own renderer and its
// own row model (monthOnMonth.ts), never the generic WidgetBucket the other
// five chart types share.
//
// SEQ-199: "grid" joins for the same reason month_on_month did. It is a rows
// by months table built from the transformation library, with its own render
// model (gridModel.ts) and its own optional `grid` config block. It reads its
// figures from the project and project-month rollups, never from `metric`.
export type PnlChartType = "KPI" | "bar" | "line" | "scatter" | "stacked_bar" | "table" | "month_on_month" | "grid";
// Phase 2 (plan 7.2, section 6.3): "latest" joins the widget vocabulary now
// that the RPC and computePnlBuckets both execute it. For a sum-family
// metric the editor's derived control offers Sum only (see
// permittedAggregationsFor); for a calculated metric it offers Average only;
// for any other registry method it offers that method alone (the restored
// pinning in checkAggregationPermitted).
export type PnlAggregation = "sum" | "avg" | "min" | "max" | "latest";
export type PnlDisplayAs = "currency" | "percent";

// Plan decision D15, two optional fields, no schemaVersion bump. `dataGrain`
// is the grain a widget asks the RPC for; `groupBy` stays the client-side
// rollup grain applied to what comes back (D16). `layout` holds size only,
// never position: order is the `position` column on pnl_dashboard_widgets
// (D18).
export interface PnlWidgetLayout {
  w: 1 | 2 | 3 | 4;
  h: 1 | 2 | 3;
}

// The widget's own grouping vocabulary, widened for D16 so one "Group by"
// control can write a real RPC grain and the renderer can read the buckets
// straight back. `PnlRollupGrain` covers everything the client rolls up
// itself (project | month | project_month | company | financial_year |
// project_manager | site_manager since phase 2); the rest are grains the
// RPC buckets server side, which the widget renders as it receives them.
export type PnlWidgetGroupBy =
  | PnlRollupGrain
  | "quarter"
  | "calendar_year"
  | "financial_year"
  | "builder"
  | "status"
  | "project_manager"
  | "site_manager";

export interface PnlWidgetFilter {
  field: string;
  op: "eq" | "in" | "gte" | "lte" | "between";
  value: string | number | boolean | Array<string | number>;
}

/** A preset name resolved at render time, or an explicit custom range. */
export interface PnlWidgetDateRange {
  preset: DatePresetName;
  custom?: DateRange;
  /**
   * D19. The id of one of this dashboard's own saved date presets
   * (`pnl_dashboards.date_presets`). Optional, and resolved against the
   * dashboard at render time: an id whose preset has since been deleted
   * falls back to the dashboard filter rather than blanking the widget.
   */
  customPresetId?: string;
}

export type PnlComparisonKind = "previous_month" | "previous_quarter" | "previous_year" | "previous_financial_year";

export interface PnlWidgetComparison {
  kind: PnlComparisonKind;
}

export interface PnlWidgetSort {
  by: string;
  direction: "asc" | "desc";
}

export interface PnlWidgetConfig {
  schemaVersion: 1;
  widgetId: string;
  name: string;
  chartType: PnlChartType;
  metric: string;
  secondaryMetric?: string;
  groupBy: PnlWidgetGroupBy;
  aggregation: PnlAggregation;
  filters: PnlWidgetFilter[];
  dateRange: PnlWidgetDateRange;
  comparison?: PnlWidgetComparison;
  sort?: PnlWidgetSort;
  limit?: number;
  displayAs?: PnlDisplayAs;
  /** D15/D16. The RPC grain this widget fetches (analyticsClient's exact vocabulary). Defaults to the page's own grain when unset. */
  dataGrain?: PnlGroupBy;
  /**
   * NOT in the plan's section 6.7 field list. Added because
   * without it the page's sticky filter bar has no date control that does
   * anything: all 13 default configs name a preset of their own, so a widget
   * reading its own `dateRange` unconditionally would ignore the filter bar
   * entirely. "dashboard" (the default, and the value every config written
   * before phase 2 resolves to) means "follow the filter bar"; "widget" means
   * "use my own `dateRange`". Optional, so D15's no-bump rule holds.
   */
  dateRangeMode?: "dashboard" | "widget";
  /** D15/D18. Column and row span in the dashboard grid. Size only, never position. */
  layout?: PnlWidgetLayout;
  /**
   * SEQ-193. `month_on_month` only. The metric keys shown as ROWS when
   * `groupBy` is "company" (multiple metrics, one company-wide value per
   * month each). Optional so no schemaVersion bump is needed (D15's rule):
   * when absent, the widget falls back to the single `metric` field, which
   * is also what keeps `metric` meaningful for validation and for the
   * "unknown metric" guard every other chart type already relies on.
   * Ignored by every other chart type.
   */
  metrics?: string[];
  /**
   * SEQ-193. `month_on_month` only. Adds a trailing "Change" column
   * comparing the table's last two calendar months, in dollars/units and as
   * a percent. Optional, defaults to off (the plan's "optional change
   * column"). Ignored by every other chart type.
   */
  showChange?: boolean;
  /**
   * SEQ-199. `grid` only. Measures, summary columns, Pareto cut-offs, sort,
   * hidden columns and conditional formatting bands (pnlGridConfig.ts).
   * Optional, validated only when present, so no schemaVersion bump is needed
   * and every config saved before this ticket stays valid. Ignored by every
   * other chart type.
   */
  grid?: PnlGridConfig;
}

const CHART_TYPES: readonly PnlChartType[] = ["KPI", "bar", "line", "scatter", "stacked_bar", "table", "month_on_month", "grid"];
const AGGREGATIONS: readonly PnlAggregation[] = ["sum", "avg", "min", "max", "latest"];
const DISPLAY_AS: readonly PnlDisplayAs[] = ["currency", "percent"];
// Widened for D16/P23: the client-side rollup grains (`PnlRollupGrain`) plus
// the RPC grains a widget can fetch and render directly.
const GROUP_BYS: readonly PnlWidgetGroupBy[] = [
  "project",
  "month",
  "project_month",
  "company",
  "quarter",
  "calendar_year",
  "financial_year",
  "builder",
  "status",
  "project_manager",
  "site_manager",
];
/** The exact `layout.w` and `layout.h` values the grid CSS and the D13 SQL CHECK both admit. */
const LAYOUT_WIDTHS: readonly number[] = [1, 2, 3, 4];
const LAYOUT_HEIGHTS: readonly number[] = [1, 2, 3];
const FILTER_OPS: readonly PnlWidgetFilter["op"][] = ["eq", "in", "gte", "lte", "between"];

// ─── Phase 2, plan section 7.3: the validation message catalogue ───────────
// The builder renders these inline under the offending field. They are
// exported from here, next to the rules that raise them, so the message and
// the rule cannot drift; the validators below attach them to `issues` with a
// stable code and field name, and the UI reads those rather than matching
// on text.

/** The unit chips the metric selector shows (plan section 7.2). */
export const PNL_UNIT_CHIP: Record<PnlUnit, string> = {
  currency: "$",
  percent: "%",
  hours: "hrs",
  count: "count",
  ratio: "ratio",
  currency_per_hour: "$/hr",
};

/** How each refused aggregation reads in a sentence ("cannot be summed across buckets"), the plan 7.3 wording (E2E low 4 replaced the apostrophe-d form). */
const AGGREGATION_VERB: Record<PnlAggregation, string> = {
  sum: "summed",
  avg: "averaged",
  min: "reduced to a minimum",
  max: "reduced to a maximum",
  latest: "reduced to a latest value",
};

export const PNL_WIDGET_VALIDATION_MESSAGES = {
  /** Sum (or min, max, latest) on a calculated metric, the plan 7.3 wording: "cannot be summed across buckets". */
  calculatedMetricAggregation: (metricKey: string, aggregation: PnlAggregation): string =>
    `metric "${metricKey}" has aggregation_method "calculated" and cannot be ${AGGREGATION_VERB[aggregation]} ` +
    `across buckets; average is the only cross-bucket rollup a calculated metric permits`,
  /** A registry method other than sum pins the widget to that method (the restored min/max/latest pinning, extended to avg). */
  aggregationPinned: (metricKey: string, method: PnlAggregationMethod, aggregation: PnlAggregation): string =>
    `metric "${metricKey}" has aggregation_method "${method}" and must be shown as ${method}, not ${aggregation}; ` +
    `the RPC executes the registry's own method, so a different label would mislabel the number`,
  /** Mixed units on a single-axis chart. Callers pass display names; the validator passes metric keys. */
  mixedUnits: (primaryName: string, primaryUnit: PnlUnit, secondaryName: string, secondaryUnit: PnlUnit): string =>
    `${primaryName} (${PNL_UNIT_CHIP[primaryUnit]}) and ${secondaryName} (${PNL_UNIT_CHIP[secondaryUnit]}) cannot share one axis. ` +
    `Use a scatter, which has two axes, or pick metrics with the same unit.`,
  unknownMetric: "This metric no longer exists in the registry. Pick a replacement.",
  blankName: "Give the widget a name.",
  customRangeIncomplete: "Choose both a from month and a to month.",
  comparisonOnLifetime: "A lifetime range has no prior period to compare against.",
} as const;

export type PnlWidgetIssueCode =
  | "calculated_metric_aggregation"
  | "aggregation_pinned"
  | "mixed_units"
  | "unknown_metric"
  | "blank_name"
  | "custom_range_incomplete"
  | "comparison_on_lifetime";

export type PnlWidgetIssueField = "name" | "metric" | "secondaryMetric" | "aggregation" | "dateRange" | "comparison";

/** One user-facing validation finding: a stable code, the field to render it under, and the catalogue message. */
export interface PnlWidgetIssue {
  code: PnlWidgetIssueCode;
  field: PnlWidgetIssueField;
  message: string;
}
const DATE_PRESETS: readonly DatePresetName[] = [
  "this_month",
  "last_month",
  "quarter_to_date",
  "year_to_date",
  "financial_year_to_date",
  "last_financial_year",
  "project_lifetime",
  "custom",
];

export interface WidgetValidationResult {
  valid: boolean;
  /** Every finding as a developer-readable string (structural and registry rules alike). */
  errors: string[];
  /**
   * Phase 2: the findings that map to a section 7.3 message, with a field
   * to render under. Usually a subset of `errors`, but a soft issue
   * (`comparison_on_lifetime`) can be present while `valid` is true: the
   * config is still readable and renders, with the message shown inline.
   */
  issues: PnlWidgetIssue[];
}

/** Structural + enum validation. Does not check that `metric` is a known registry key (that needs the registry). */
export function validateWidgetConfig(config: unknown): WidgetValidationResult {
  const errors: string[] = [];
  const issues: PnlWidgetIssue[] = [];
  if (typeof config !== "object" || config === null) {
    return { valid: false, errors: ["config must be an object"], issues: [] };
  }
  const c = config as Record<string, unknown>;

  if (c.schemaVersion !== 1) errors.push("schemaVersion must be 1");
  if (typeof c.widgetId !== "string" || c.widgetId.length === 0) errors.push("widgetId must be a non-empty string");
  if (typeof c.name !== "string" || c.name.trim().length === 0) {
    errors.push("name must be a non-empty string");
    issues.push({ code: "blank_name", field: "name", message: PNL_WIDGET_VALIDATION_MESSAGES.blankName });
  }
  if (!CHART_TYPES.includes(c.chartType as PnlChartType)) {
    errors.push(`chartType must be one of ${CHART_TYPES.join(", ")}`);
  }
  if (typeof c.metric !== "string" || c.metric.length === 0) errors.push("metric must be a non-empty string");
  if (c.secondaryMetric !== undefined && typeof c.secondaryMetric !== "string") {
    errors.push("secondaryMetric must be a string when present");
  }
  if (!GROUP_BYS.includes(c.groupBy as PnlWidgetGroupBy)) {
    errors.push(`groupBy must be one of ${GROUP_BYS.join(", ")}`);
  }
  if (!AGGREGATIONS.includes(c.aggregation as PnlAggregation)) {
    errors.push(`aggregation must be one of ${AGGREGATIONS.join(", ")}`);
  }

  if (!Array.isArray(c.filters)) {
    errors.push("filters must be an array");
  } else {
    c.filters.forEach((f, i) => {
      if (typeof f !== "object" || f === null) {
        errors.push(`filters[${i}] must be an object`);
        return;
      }
      const filter = f as Record<string, unknown>;
      if (typeof filter.field !== "string" || filter.field.length === 0) {
        errors.push(`filters[${i}].field must be a non-empty string`);
      }
      if (!FILTER_OPS.includes(filter.op as PnlWidgetFilter["op"])) {
        errors.push(`filters[${i}].op must be one of ${FILTER_OPS.join(", ")}`);
      }
      if (filter.value === undefined) {
        errors.push(`filters[${i}].value is required`);
      }
    });
  }

  if (typeof c.dateRange !== "object" || c.dateRange === null) {
    errors.push("dateRange must be an object");
  } else {
    const dr = c.dateRange as Record<string, unknown>;
    if (!DATE_PRESETS.includes(dr.preset as DatePresetName)) {
      errors.push(`dateRange.preset must be one of ${DATE_PRESETS.join(", ")}`);
    }
    if (dr.preset === "custom") {
      const hasCustomRange = typeof dr.custom === "object" && dr.custom !== null;
      const hasPresetId = typeof dr.customPresetId === "string" && dr.customPresetId.length > 0;
      if (!hasCustomRange && !hasPresetId) {
        errors.push('dateRange.custom or dateRange.customPresetId is required when preset is "custom"');
        issues.push({ code: "custom_range_incomplete", field: "dateRange", message: PNL_WIDGET_VALIDATION_MESSAGES.customRangeIncomplete });
      } else if (hasCustomRange) {
        // Phase 2 (7.3): an explicit custom range needs both bounds. A
        // lifetime range is expressed by the project_lifetime preset, never
        // by a custom range with a missing end.
        const custom = dr.custom as Record<string, unknown>;
        if (typeof custom.from !== "string" || custom.from.length === 0 || typeof custom.to !== "string" || custom.to.length === 0) {
          errors.push("dateRange.custom must carry both from and to");
          issues.push({ code: "custom_range_incomplete", field: "dateRange", message: PNL_WIDGET_VALIDATION_MESSAGES.customRangeIncomplete });
        }
      }
    }
    if (dr.customPresetId !== undefined && (typeof dr.customPresetId !== "string" || dr.customPresetId.length === 0)) {
      errors.push("dateRange.customPresetId must be a non-empty string when present");
    }
  }

  if (c.dateRangeMode !== undefined && c.dateRangeMode !== "dashboard" && c.dateRangeMode !== "widget") {
    errors.push('dateRangeMode must be "dashboard" or "widget" when present');
  }

  if (c.comparison !== undefined) {
    if (typeof c.comparison !== "object" || c.comparison === null) {
      errors.push("comparison must be an object when present");
    } else {
      const comparisonKinds: readonly PnlComparisonKind[] = [
        "previous_month",
        "previous_quarter",
        "previous_year",
        "previous_financial_year",
      ];
      const kind = (c.comparison as Record<string, unknown>).kind;
      if (!comparisonKinds.includes(kind as PnlComparisonKind)) {
        errors.push(`comparison.kind must be one of ${comparisonKinds.join(", ")}`);
      }
      // Phase 2 (7.3): project lifetime has no prior period, so a
      // comparison on it can never resolve (fiscalYear.ts's comparisonPeriod
      // needs a bounded range).
      // A SOFT issue, not a structural error (team lead decision on the
      // P8 report, item 8): a saved widget must never be dropped for a
      // comparison it cannot honour. The config stays valid and readable;
      // the renderer shows the 7.3 message and runs the widget without
      // its comparison.
      const preset = typeof c.dateRange === "object" && c.dateRange !== null ? (c.dateRange as Record<string, unknown>).preset : undefined;
      if (preset === "project_lifetime") {
        issues.push({ code: "comparison_on_lifetime", field: "comparison", message: PNL_WIDGET_VALIDATION_MESSAGES.comparisonOnLifetime });
      }
    }
  }

  if (c.sort !== undefined) {
    if (typeof c.sort !== "object" || c.sort === null) {
      errors.push("sort must be an object when present");
    } else {
      const sort = c.sort as Record<string, unknown>;
      if (typeof sort.by !== "string" || sort.by.length === 0) errors.push("sort.by must be a non-empty string");
      if (sort.direction !== "asc" && sort.direction !== "desc") errors.push('sort.direction must be "asc" or "desc"');
    }
  }

  if (c.limit !== undefined && (typeof c.limit !== "number" || c.limit <= 0)) {
    errors.push("limit must be a positive number when present");
  }

  if (c.displayAs !== undefined && !DISPLAY_AS.includes(c.displayAs as PnlDisplayAs)) {
    errors.push(`displayAs must be one of ${DISPLAY_AS.join(", ")}`);
  }

  // D15's two optional fields. Both are validated only when present, so
  // every config written before phase 2 stays valid and no schemaVersion
  // bump is needed. dataGrain is checked against the RPC's exact vocabulary
  // (D16), so a stale client-only grain like project_month cannot be saved
  // as something to fetch.
  //
  // SEQ-193: project_month is now an RPC grain, but only the Project
  // performance table's By month view fetches it, with its own call. A saved
  // widget must not be able to name it as its data grain, so it is excluded
  // here and the saved-widget vocabulary stays what it was.
  const widgetDataGrains = PNL_GROUP_BY_VALUES.filter((value) => value !== "project_month");
  if (c.dataGrain !== undefined && !(widgetDataGrains as readonly unknown[]).includes(c.dataGrain)) {
    errors.push(`dataGrain must be one of ${widgetDataGrains.join(", ")} when present`);
  }

  if (c.layout !== undefined) {
    if (typeof c.layout !== "object" || c.layout === null) {
      errors.push("layout must be an object when present");
    } else {
      const layout = c.layout as Record<string, unknown>;
      if (!LAYOUT_WIDTHS.includes(layout.w as number)) {
        errors.push(`layout.w must be one of ${LAYOUT_WIDTHS.join(", ")}`);
      }
      if (!LAYOUT_HEIGHTS.includes(layout.h as number)) {
        errors.push(`layout.h must be one of ${LAYOUT_HEIGHTS.join(", ")}`);
      }
    }
  }

  // SEQ-193: both optional, validated only when present, so no config
  // written before this ticket needs a schemaVersion bump (D15's rule).
  if (c.metrics !== undefined) {
    if (!Array.isArray(c.metrics) || c.metrics.some((m) => typeof m !== "string" || m.length === 0)) {
      errors.push("metrics must be an array of non-empty strings when present");
    }
  }
  if (c.showChange !== undefined && typeof c.showChange !== "boolean") {
    errors.push("showChange must be a boolean when present");
  }

  // SEQ-199: the grid block is checked whenever it is present, whatever the
  // chart type, so a malformed block cannot sit in a saved row waiting for
  // someone to switch the widget to a grid.
  errors.push(...validateGridConfig(c.grid));

  return { valid: errors.length === 0, errors, issues };
}

// ─── The default dashboard ──────────────────────────────────────────────────
// Plan decision D11: the default dashboard is CODE, not seeded rows. When a
// user has no dashboards and the organisation has no shared one, the page
// renders these configs read only as "Company overview", with a "Save a copy"
// that writes real rows. A seed in a migration would mean a migration to
// change a default chart, a blank page for anyone who deleted a row, and a
// seed drifting from the constants this file's own tests assert.
//
// D12: a KPI widget is ONE card, and `metric` is singular by design, so the
// phase 1 page's eight cards are eight configs. Eight KPI configs plus the
// two scatters, the bar, the trend line and the table is 13, not 9. The
// alternative, a `metrics: string[]` field so one KPI widget draws many
// cards, was rejected: it would need its own validation rules for units per
// card, which is the exact class of error the singular field prevents.
//
// LAYOUT AND DATA GRAIN. `layout` reproduces the phase 1 page's shape in the
// four column grid: eight quarter-width cards (two rows of four), two
// half-width scatters, a half-width bar beside a half-width trend line, then
// the full-width table. `dataGrain` is the grain each widget FETCHES; the
// KPI cards fetch at project grain and roll up to a company total client
// side, which is exactly what the phase 1 page did, so the numbers on the
// cards are the same numbers, not a server-side re-aggregation of them.
//
// The order below is the order the phase 1 page renders in, top to bottom.

const DEFAULT_DATE_RANGE: PnlWidgetDateRange = { preset: "financial_year_to_date" };

/** Every KPI card: one card wide, one row tall, fetched at project grain and summed to a company total. */
const KPI_LAYOUT: PnlWidgetLayout = { w: 1, h: 1 };
/** A chart that sits two to a row, as both scatter rows and the bar/trend row do today. */
const HALF_LAYOUT: PnlWidgetLayout = { w: 2, h: 2 };
/** The project table, full width and tall. */
const TABLE_LAYOUT: PnlWidgetLayout = { w: 4, h: 3 };

export const PHASE1_WIDGETS: PnlWidgetConfig[] = [
  {
    schemaVersion: 1,
    widgetId: "kpi-revenue",
    name: "Revenue",
    chartType: "KPI",
    metric: "calculated.total_income",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "sum",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "currency",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "kpi-net-profit",
    name: "Net profit",
    chartType: "KPI",
    metric: "calculated.net_profit",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "sum",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "currency",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "kpi-profit-percent",
    name: "Profit %",
    chartType: "KPI",
    metric: "calculated.profit_percentage",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "avg",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "percent",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "kpi-total-cost",
    name: "Total cost",
    chartType: "KPI",
    metric: "calculated.total_cost",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "sum",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "currency",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "kpi-overtime-cost",
    name: "Overtime cost",
    chartType: "KPI",
    metric: "labour.overtime",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "sum",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "currency",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "kpi-overtime-pct-revenue",
    name: "Overtime % revenue",
    chartType: "KPI",
    metric: "calculated.overtime_pct_revenue",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "avg",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "percent",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "kpi-patching-cost",
    name: "Patching cost",
    chartType: "KPI",
    metric: "expense.patching",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "sum",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "currency",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "kpi-patching-pct-revenue",
    name: "Patching % revenue",
    chartType: "KPI",
    metric: "calculated.patching_pct_revenue",
    groupBy: "company",
    dataGrain: "project",
    aggregation: "avg",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "percent",
    layout: KPI_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "scatter-profit-vs-overtime",
    name: "Profit vs overtime",
    chartType: "scatter",
    metric: "calculated.overtime_pct_revenue",
    secondaryMetric: "calculated.profit_percentage",
    groupBy: "project",
    dataGrain: "project",
    aggregation: "avg",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "percent",
    layout: HALF_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "scatter-profit-vs-patching",
    name: "Profit vs patching",
    chartType: "scatter",
    metric: "calculated.patching_pct_revenue",
    secondaryMetric: "calculated.profit_percentage",
    groupBy: "project",
    dataGrain: "project",
    aggregation: "avg",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "percent",
    layout: HALF_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "bar-profit-percent-by-project",
    name: "Profit % by project",
    chartType: "bar",
    metric: "calculated.profit_percentage",
    groupBy: "project",
    dataGrain: "project",
    aggregation: "avg",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    sort: { by: "calculated.profit_percentage", direction: "desc" },
    displayAs: "percent",
    layout: HALF_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "line-monthly-trend",
    name: "Monthly trend",
    chartType: "line",
    metric: "calculated.profit_percentage",
    secondaryMetric: "calculated.overtime_pct_revenue",
    groupBy: "month",
    dataGrain: "month",
    aggregation: "avg",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    displayAs: "percent",
    layout: HALF_LAYOUT,
  },
  {
    schemaVersion: 1,
    widgetId: "table-project-performance",
    name: "Project performance",
    chartType: "table",
    metric: "calculated.net_profit",
    groupBy: "project",
    dataGrain: "project",
    aggregation: "sum",
    filters: [],
    dateRange: DEFAULT_DATE_RANGE,
    sort: { by: "calculated.net_profit", direction: "desc" },
    displayAs: "currency",
    layout: TABLE_LAYOUT,
  },
];

/** The name the default dashboard shows in the dashboard select (plan decision D11). */
export const DEFAULT_DASHBOARD_NAME = "Company overview";

// ─── SEQ-182: widget/metric compatibility (Dashboard Compatibility Rules) ──

/** A metricKey -> unit map for the client, e.g. to label a metric picker with its unit ("Overtime hrs", "Profit %"). */
export function metricUnitLookup(registry: PnlMetricDefinition[]): Record<string, PnlUnit> {
  const lookup: Record<string, PnlUnit> = {};
  for (const def of registry) {
    lookup[def.metricKey] = resolveMetricUnit(def);
  }
  return lookup;
}

/**
 * Checks one metric's resolved aggregation_method against the widget's
 * chosen `aggregation`, returning an error string or null. One rule,
 * matching the ticket's Dashboard Compatibility Rules section: a calculated
 * percent, ratio, or rate metric (e.g. Profit %, or a currency_per_hour
 * rate) can never be summed, min'd, or max'd across buckets (the ticket's
 * own "Invalid: SUM(Profit %)" example). `avg` is left alone: averaging a
 * percentage across projects is what every phase 1 widget already does
 * (`kpi-profit-percent`, `bar-profit-percent-by-project`), and the ticket
 * only names SUM as the invalid case.
 *
 * Round 4 (security audit) removed the min/max/latest pinning branch when
 * `PnlAggregationMethod` was narrowed to `sum | calculated`. SEQ-180 phase 2
 * (plan section 6.4, F15) restores it now that the type and the RPC admit
 * all six values again, and extends it to `avg`: a registry method other
 * than `sum` or `calculated` pins the widget's aggregation to exactly that
 * method, because the RPC executes the registry's own method and a widget
 * labelled "max" over avg-computed numbers would mislabel every value. A
 * `sum` metric is the only kind that offers all five (plan 7.2's "sum
 * family").
 */
function checkAggregationPermitted(def: PnlMetricDefinition, aggregation: PnlAggregation): PnlWidgetIssue | null {
  const method = resolveAggregationMethod(def);

  // Round 2 (code review, HIGH): this used to also require the metric's unit
  // to be percent/ratio before rejecting sum, which let sum through on the
  // three currency_per_hour rates (calculated.labour_cost_per_hour,
  // revenue_per_labour_hour, profit_per_labour_hour) -- they are just as
  // meaningless to sum as a percentage is (summing three projects' dollars
  // per hour is not a dollars-per-hour figure). The rule is now
  // unit-independent: ANY metric whose own aggregation_method is 'calculated'
  // can never be summed, min'd, max'd or snapshotted, full stop. `avg` is
  // deliberately left unrestricted here, which is what keeps every phase 1
  // widget that already averages a calculated percentage (kpi-profit-percent,
  // bar-profit-percent-by-project, both overtime/patching scatters, the
  // monthly trend line) valid. Phase 2 adds `latest` to the rejected list.
  if (method === "calculated" && aggregation !== "avg") {
    return {
      code: "calculated_metric_aggregation",
      field: "aggregation",
      message: PNL_WIDGET_VALIDATION_MESSAGES.calculatedMetricAggregation(def.metricKey, aggregation),
    };
  }
  if (method !== "sum" && method !== "calculated" && aggregation !== method) {
    return {
      code: "aggregation_pinned",
      field: "aggregation",
      message: PNL_WIDGET_VALIDATION_MESSAGES.aggregationPinned(def.metricKey, method, aggregation),
    };
  }
  return null;
}

function permittedAggregationsForOne(def: PnlMetricDefinition): readonly PnlAggregation[] {
  const method = resolveAggregationMethod(def);
  if (method === "sum") return AGGREGATIONS;
  if (method === "calculated") return ["avg"];
  return [method];
}

/**
 * Phase 2 (plan 7.2): the aggregations the builder's control may offer for
 * a widget, derived from the registry so an invalid pair cannot be composed
 * and then rejected. A `sum` metric offers Sum only (SEQ-240 audit B, row 20:
 * the chart reads each bucket's total from the database, so Average, Minimum,
 * Maximum and Latest of a sum metric would still show the total under another
 * name; the validator keeps accepting them so a saved widget still loads); a
 * `calculated` metric offers Average only; any other method offers itself only (the pinning
 * rule in checkAggregationPermitted, stated positively). With a secondary
 * metric set (code review M4) the offer is the intersection of the two
 * metrics' own sets, because `validateWidgetMetricCompatibility` checks the
 * one `aggregation` against both; the intersection can be empty, which the
 * control shows as "no aggregation fits both metrics".
 */
export function permittedAggregationsFor(def: PnlMetricDefinition, secondaryDef?: PnlMetricDefinition): readonly PnlAggregation[] {
  const primary = permittedAggregationsForOne(def);
  const offered = secondaryDef
    ? primary.filter((a) => permittedAggregationsForOne(secondaryDef).includes(a))
    : primary;
  // Only the sum family has all five; the control narrows that to Sum.
  return offered.length === AGGREGATIONS.length ? SUM_ONLY : offered;
}

const SUM_ONLY: readonly PnlAggregation[] = ["sum"];

/**
 * SEQ-193. The metric keys a `month_on_month` widget shows as rows in
 * "company" `groupBy` mode: `metrics` when the config sets it, otherwise the
 * single `metric` field, deduplicated and never empty. One place for this
 * fallback rule, so the editor, the validator and the renderer cannot
 * disagree about which metrics a saved widget actually shows.
 */
export function monthOnMonthMetricKeys(config: Pick<PnlWidgetConfig, "metric" | "metrics">): string[] {
  const list = config.metrics && config.metrics.length > 0 ? config.metrics : [config.metric];
  return [...new Set(list)];
}

/**
 * Registry-aware validation `validateWidgetConfig` deliberately does not do
 * (its own comment says so): whether `metric` and `secondaryMetric` are
 * known keys, whether the chosen `aggregation` is one the metric permits,
 * and whether mixing units across the two metrics is safe for the chosen
 * chart type. Kept as a second function so the existing structural-only
 * test suite for `validateWidgetConfig` stays green.
 */
export function validateWidgetMetricCompatibility(
  config: PnlWidgetConfig,
  registry: PnlMetricDefinition[],
): WidgetValidationResult {
  const errors: string[] = [];
  const issues: PnlWidgetIssue[] = [];
  const registryByKey = new Map(registry.map((def) => [def.metricKey, def]));

  // SEQ-199: a grid reads its figures from the project and project-month
  // rollups by measure key (pnlGridConfig.ts), never from `metric`,
  // `aggregation` or `secondaryMetric`. There is nothing here to check
  // against the registry, and a registry change must not blank a saved grid.
  if (config.chartType === "grid") return { valid: true, errors, issues };

  // SEQ-193: month_on_month reads each of its metrics straight from the
  // fetched bucket, one value per calendar month, and never folds one
  // metric's values into another the way a KPI or a shared-axis chart does.
  // So the aggregation-pinning rule (which is about combining several
  // buckets into one) and the mixed-units rule (which is about two metrics
  // sharing one axis) both stop applying; the only thing worth checking is
  // that every metric it names still exists in the registry, exactly like
  // `metric`/`secondaryMetric` above.
  if (config.chartType === "month_on_month") {
    if (config.groupBy === "project") {
      const def = registryByKey.get(config.metric);
      if (!def) {
        errors.push(`metric "${config.metric}" is not a known registry key`);
        issues.push({ code: "unknown_metric", field: "metric", message: PNL_WIDGET_VALIDATION_MESSAGES.unknownMetric });
      }
    } else {
      for (const key of monthOnMonthMetricKeys(config)) {
        if (!registryByKey.has(key)) {
          errors.push(`metric "${key}" is not a known registry key`);
          issues.push({ code: "unknown_metric", field: "metric", message: PNL_WIDGET_VALIDATION_MESSAGES.unknownMetric });
        }
      }
    }
    return { valid: errors.length === 0, errors, issues };
  }

  const primaryDef = registryByKey.get(config.metric);
  if (!primaryDef) {
    errors.push(`metric "${config.metric}" is not a known registry key`);
    issues.push({ code: "unknown_metric", field: "metric", message: PNL_WIDGET_VALIDATION_MESSAGES.unknownMetric });
  }

  let secondaryDef: PnlMetricDefinition | undefined;
  if (config.secondaryMetric !== undefined) {
    secondaryDef = registryByKey.get(config.secondaryMetric);
    if (!secondaryDef) {
      errors.push(`secondaryMetric "${config.secondaryMetric}" is not a known registry key`);
      issues.push({ code: "unknown_metric", field: "secondaryMetric", message: PNL_WIDGET_VALIDATION_MESSAGES.unknownMetric });
    }
  }

  if (primaryDef) {
    const aggIssue = checkAggregationPermitted(primaryDef, config.aggregation);
    if (aggIssue) {
      errors.push(aggIssue.message);
      issues.push(aggIssue);
    }
  }
  if (secondaryDef) {
    const aggIssue = checkAggregationPermitted(secondaryDef, config.aggregation);
    if (aggIssue) {
      errors.push(`secondaryMetric: ${aggIssue.message}`);
      issues.push({ ...aggIssue, field: "secondaryMetric" });
    }
  }

  if (primaryDef && secondaryDef) {
    const primaryUnit = resolveMetricUnit(primaryDef);
    const secondaryUnit = resolveMetricUnit(secondaryDef);
    if (primaryUnit !== secondaryUnit) {
      // A scatter draws two independent axes by definition, so mixed units
      // are exactly the point (AC08: Profit % vs Overtime Hours). A KPI with
      // an explicit comparison is the other sanctioned case (e.g. "this
      // month's Profit % vs last month's Net Profit $"). Every other chart
      // type draws both metrics on one shared axis, where mixed units are
      // meaningless (a dollar bar next to an hours bar on the same scale).
      const twoAxisChart = config.chartType === "scatter";
      const kpiWithComparison = config.chartType === "KPI" && config.comparison !== undefined;
      if (!twoAxisChart && !kpiWithComparison) {
        errors.push(
          `metric ("${config.metric}", unit "${primaryUnit}") and secondaryMetric ` +
            `("${config.secondaryMetric}", unit "${secondaryUnit}") have different units; only a scatter chart ` +
            `(two independent axes) or a KPI with an explicit comparison can mix units`,
        );
        // The validator only knows keys; the builder re-renders this
        // message with display names via the same catalogue function.
        issues.push({
          code: "mixed_units",
          field: "secondaryMetric",
          message: PNL_WIDGET_VALIDATION_MESSAGES.mixedUnits(config.metric, primaryUnit, config.secondaryMetric as string, secondaryUnit),
        });
      }
    }
  }

  return { valid: errors.length === 0, errors, issues };
}
